import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useState,
} from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { Tab } from "semantic-ui-react";

import { getLocalStorageItem } from "simplydo/core/helpers";
import type { OpenAPI } from "simplydo/interfaces";
import { useThrottle } from "simplydo/react/hooks";

import api from "api";

import ImpactStatementView from "./ImpactStatementView";
import { StageSegment } from "./StageSegments";

type Challenge = OpenAPI.GET<"/challenges/{id}">["response"];
type ImpactStatements =
  OpenAPI.GET<"/ideas/{idea_id}/impactStatement">["response"]["impactStatements"];

type ImpactStatementProps = {
  impactStatements: ImpactStatements;
  setImpactStatements: Dispatch<SetStateAction<ImpactStatements>>;
  forStage?: OpenAPI.Schemas["ImpactStatement"]["stage"];
  statementDefintion: Challenge["impactStatements"];
  restrictStagesTo?: OpenAPI.Schemas["ImpactStatement"]["stage"][];
  ideaId: string;
  challengeId: string;
  editable?: boolean;
  canManageIdea?: boolean;
};

export const impactStageDefinitions: Record<
  NonNullable<OpenAPI.Schemas["ImpactStatement"]["stage"]>,
  {
    name: string;
    previousStage?: NonNullable<OpenAPI.Schemas["ImpactStatement"]["stage"]>;
    rangeInputType?: "auto" | "allowCustom";
  }
> = {
  // The user stage is the first stage where users can estimate the impact of their idea
  // The input type is exactly as configured in the challenge
  userEstimate: { name: "User Estimate", rangeInputType: "auto" },
  // In the project estimation phase the project team can provide their own estimate
  projectEstimate: {
    name: "Project Estimate",
    previousStage: "userEstimate",
    rangeInputType: "auto",
  },
  // For the final outcome the input type is allowed to have a custom input value
  finalOutcome: {
    name: "Final Outcome",
    previousStage: "projectEstimate",
    rangeInputType: "allowCustom",
  },
};
export const impactStageOrder = [
  "userEstimate",
  "projectEstimate",
  "finalOutcome",
] as const;

export const ImpactStatementEditor = ({
  impactStatements,
  setImpactStatements,
  statementDefintion,
  forStage,
  restrictStagesTo: availableStages,
  ideaId,
  challengeId,
  editable,
  canManageIdea,
}: ImpactStatementProps) => {
  const authToken = getLocalStorageItem(`ideaAuthToken:${ideaId}`);
  const { t } = useTranslation();
  const existingStages = Object.keys(
    impactStatements,
  ) as OpenAPI.Schemas["ImpactStatement"]["stage"][];
  const [stage, setStage] = useState(forStage || existingStages[0]);
  const [isCreatingStage, setIsCreatingStage] = useState(null);

  const createImpactStatement = useCallback(
    (
      newStatement: ImpactStatements["userEstimate"],
      forStage: OpenAPI.Schemas["ImpactStatement"]["stage"],
    ) => {
      setIsCreatingStage(forStage);
      api.ideas.createImpactStatement(
        ideaId,
        {
          impacts: newStatement.impacts,
          description: newStatement.description,
          stage: forStage,
        },
        authToken,
        ({ impactStatement: newImpactStatement }) => {
          setIsCreatingStage(null);
          setImpactStatements({
            ...impactStatements,
            [forStage]: newImpactStatement,
          });
        },
        () => {
          setIsCreatingStage(null);
          toast.error(
            `Failed to save ${t("generic.impact")} statement. Please try again.`,
          );
          setImpactStatements({ ...impactStatements, [forStage]: undefined });
        },
      );
    },
    [ideaId, authToken, impactStatements, setImpactStatements, t],
  );

  const saveImpactStatement = useThrottle(
    (newStatement: ImpactStatements["userEstimate"]) => {
      api.ideas.updateImpactStatement(
        ideaId,
        newStatement._id,
        {
          impacts: newStatement.impacts,
          description: newStatement.description,
        },
        authToken,
        ({ impactStatement }) => {
          api.setQueryData(api.ideas.getImpactStatements, [ideaId], (prev) => ({
            ...prev,
            impactStatement,
          }));
          api.invalidateQueries(api.challenges.getIdeas, [challengeId]);
        },
        () => {
          toast.error(
            `Failed to update ${t("generic.impact")} statement. Please try again.`,
          );
        },
      );
    },
    400,
    [ideaId],
  );

  const updateImpactStatement = useCallback(
    (newStatement: ImpactStatements["userEstimate"]) => {
      if (newStatement._id) {
        setImpactStatements({ ...impactStatements, [stage]: newStatement });
      }
      saveImpactStatement(newStatement);
    },
    [saveImpactStatement, impactStatements, setImpactStatements, stage],
  );

  if (availableStages && availableStages.length === 1) {
    const onlyStage = availableStages[0];
    return (
      <ImpactStatementView
        stage={onlyStage}
        statementDefintion={statementDefintion}
        impactStatement={impactStatements[onlyStage]}
        previousStatement={
          impactStatements[impactStageDefinitions[onlyStage].previousStage]
        }
        updateImpactStatement={updateImpactStatement}
        createImpactStatement={(statement) =>
          createImpactStatement(statement, onlyStage)
        }
        isCreating={isCreatingStage === onlyStage}
        editable={editable}
        rangeInputType={impactStageDefinitions[onlyStage].rangeInputType}
      />
    );
  }

  return (
    <Tab
      activeIndex={impactStageOrder.indexOf(stage)}
      onTabChange={(_, data) => {
        const newStage = impactStageOrder[data.activeIndex as number];
        setStage(newStage);
      }}
      menu={{ secondary: true, pointing: true }}
      panes={impactStageOrder
        .filter((s) => !availableStages || availableStages.includes(s))
        .map((s) => ({
          menuItem: impactStageDefinitions[s].name,
          render: () => {
            const canEditStage =
              editable || (canManageIdea && s !== "userEstimate");
            const previousStatement =
              impactStatements[impactStageDefinitions[s].previousStage];

            if (s !== "userEstimate" && !impactStatements[s]) {
              return (
                <StageSegment
                  stage={s}
                  statementDefintion={statementDefintion}
                  impactStatement={undefined}
                  onCreate={() => {
                    createImpactStatement(
                      {
                        impacts: previousStatement?.impacts ?? {},
                        description: previousStatement?.description,
                      },
                      s,
                    );
                  }}
                  isCreating={isCreatingStage === s}
                />
              );
            }
            return (
              <ImpactStatementView
                stage={s}
                statementDefintion={statementDefintion}
                impactStatement={impactStatements[s]}
                previousStatement={canEditStage ? previousStatement : undefined}
                updateImpactStatement={updateImpactStatement}
                createImpactStatement={(statement) =>
                  createImpactStatement(statement, s)
                }
                isCreating={isCreatingStage === s}
                editable={canEditStage}
                rangeInputType={impactStageDefinitions[s].rangeInputType}
              />
            );
          },
        }))}
    />
  );
};
